import React from 'react';
import styled from 'styled-components';
import { Button, FormHelperText, Switch, TextField } from '@material-ui/core';

import { OpeningDay, OpeningDayHours, OpeningHours } from '../../../types/surgery';
import { OPENING_DAYS, openingHoursErrors } from '../../../utils/openingHours';

interface Props {
  value?: OpeningHours | null;
  onChange: (hours: OpeningHours) => void;
  disabled?: boolean;
}

const CLOSED_DAY: OpeningDayHours = { isOpened: false, open: null, close: null, breaks: [] };
const DEFAULT_BREAK = { start: '13:00', end: '14:00' };

const OpeningHoursEditor = ({ value, onChange, disabled }: Props) => {
  const hours = value || {};
  const errors = openingHoursErrors(hours);

  const updateDay = (day: OpeningDay, changes: Partial<OpeningDayHours>) =>
    onChange({ ...hours, [day]: { ...(hours[day] || CLOSED_DAY), ...changes } });

  const toggleDay = (day: OpeningDay, isOpened: boolean) =>
    onChange({ ...hours, [day]: isOpened ? { isOpened: true, open: null, close: null, breaks: [] } : CLOSED_DAY });

  const updateBreak = (day: OpeningDay, field: 'start' | 'end', time: string) => {
    const [first, ...rest] = (hours[day] || CLOSED_DAY).breaks;
    updateDay(day, { breaks: [{ ...first, [field]: time }, ...rest] });
  };

  return (
    <div>
      {OPENING_DAYS.map((day) => {
        const { isOpened, open, close, breaks } = hours[day] || CLOSED_DAY;
        const lunch = breaks[0];

        return (
          <Row key={day}>
            <DayName>{day}</DayName>
            <Switch
              checked={isOpened}
              disabled={disabled}
              onChange={(event) => toggleDay(day, event.target.checked)}
              inputProps={{ 'aria-label': `${day} open` }}
            />
            <TimeField
              type="time"
              value={open || ''}
              disabled={disabled || !isOpened}
              onChange={(event) => updateDay(day, { open: event.target.value || null })}
              inputProps={{ 'aria-label': `${day} opens at` }}
            />
            <TimeField
              type="time"
              value={close || ''}
              disabled={disabled || !isOpened}
              onChange={(event) => updateDay(day, { close: event.target.value || null })}
              inputProps={{ 'aria-label': `${day} closes at` }}
            />
            {isOpened && !lunch && (
              <Button
                size="small"
                disabled={disabled}
                onClick={() => updateDay(day, { breaks: [DEFAULT_BREAK] })}
                aria-label={`Add ${day} break`}
              >
                Add break
              </Button>
            )}
            {isOpened && lunch && (
              <>
                <TimeField
                  type="time"
                  value={lunch.start}
                  disabled={disabled}
                  onChange={(event) => updateBreak(day, 'start', event.target.value)}
                  inputProps={{ 'aria-label': `${day} break starts at` }}
                />
                <TimeField
                  type="time"
                  value={lunch.end}
                  disabled={disabled}
                  onChange={(event) => updateBreak(day, 'end', event.target.value)}
                  inputProps={{ 'aria-label': `${day} break ends at` }}
                />
                <Button
                  size="small"
                  disabled={disabled}
                  onClick={() => updateDay(day, { breaks: breaks.slice(1) })}
                  aria-label={`Remove ${day} break`}
                >
                  Remove break
                </Button>
              </>
            )}
            {errors[day] && <DayError error>{errors[day]}</DayError>}
          </Row>
        );
      })}
    </div>
  );
};

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 4px 0;
`;

const DayName = styled.span`
  width: 120px;
`;

const TimeField = styled(TextField)`
  && {
    width: 110px;
    margin-right: 12px;
  }
`;

const DayError = styled(FormHelperText)`
  && {
    flex-basis: 100%;
    margin: 0 0 4px 120px;
  }
`;

export default OpeningHoursEditor;
